import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { setRoom } from '@/graph/patients';

const COOLDOWN_MS = 2000;

// Module-level set shared across all hook instances.
// Prevents concurrent SetRoom calls for the same patient even when
// multiple components (e.g. PatientLocationWidget + OperationRoomTablet)
// are mounted simultaneously.
const inFlight = new Set<number>();

/**
 * Wraps the setRoomForPatient mutation with global deduplication.
 * Uses a module-level Set for synchronous in-flight tracking — prevents
 * rapid-fire calls that slip through before React re-renders can disable buttons.
 *
 * On success: enforces a 2s cooldown before allowing another call for the same patient.
 * On error: clears immediately to allow retry.
 * Duplicate calls return { data: { setRoomForPatient: false } } which existing
 * callers already handle with a "Room change is already in progress" toast.
 */
export function useSetRoomMutation() {
  const [mutate] = useMutation(setRoom);

  const wrappedMutate = useCallback(
    async (options: { variables: { id: number; roomId: number } }) => {
      const patientId = options.variables.id;

      if (inFlight.has(patientId)) {
        return { data: { setRoomForPatient: false } };
      }

      inFlight.add(patientId);
      try {
        const result = await mutate(options);
        setTimeout(() => {
          inFlight.delete(patientId);
        }, COOLDOWN_MS);
        return result;
      } catch (e) {
        inFlight.delete(patientId);
        throw e;
      }
    },
    [mutate]
  );

  return [wrappedMutate] as const;
}
